import { createReducer } from '@reduxjs/toolkit';
import _ from 'lodash';
import { randomId, randomInt } from '../../../reducers/util';
import { Group, Period, Report, ReportConfig } from '../types/CohortReport';
import { SavedReport } from '../types/SavedReport';

interface ActiveTab {
    tab: string
    group: Group
    period: Period
}

export interface HRUState {
    report: Report | undefined
    reports: SavedReport[]
    active: ActiveTab
}

export const emptyCohortReport = (queryId: number): Report => {
    return {
        id: randomInt(),
        createdOn: null,
        updatedOn: null,
        active: true,
        title: 'default report title',
        queryId: queryId,
        reportConfig: {
            ip: true,
            op: false,
            aande: false,
            showTotals: true,
            showPerPatientsPerYear: true,
            showPerPatient: false,
            showPerEvent: false,
            periods: [{ id: randomId(), label: 'Period', value: 'all', pre: true, at: true, post: true }],
            group1: [],
            group2: [],
            group3: []
        },
        sparkQuery: null
    }
}

export function emptyPeriod(): Period {
    return {
        id: undefined,
        value: '_null',
        label: '_null',
        pre: false,
        at: false,
        post: false
    }
}

export const emptyGroup = (level: string): Group => {
    return {
        id: randomId(),
        level: level,
        title: 'default group title',
        primaryDiagnosis: [],
        diagnonsisOr: true,
        secondaryDiagnosis: [],
        treatments: [],
        procedures: [],
        fromAge: undefined,
        toAge: undefined,
        gender: [],
        trusts: [],
        subICBLocations: [],
    }
}

const initialState: HRUState = {
    report: undefined,
    reports: [],
    active: {
        tab: "1",
        group: emptyGroup("1"),
        period: emptyPeriod()
    }
}

const hru = createReducer(
    initialState,
    {
        REPORT_SET_SAVED_REPORTS: (state, action) => {
            state.reports = action.reports
        },
        REPORT_SET_CURRENT_REPORT: (state, action) => {
            state.report = action.report
            state.active.tab = "1"
            state.active.group = emptyGroup("1")
            state.active.period = emptyPeriod()
        },
        REPORT_SET_SHOW_DATA_SOURCE: (state, action) => {
            if (!_.isNil(state.report)) {
                state.report.reportConfig[action.dataSource as keyof ReportConfig] = action.checked
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_SET_REPORT_TITLE: (state, action) => {
            if (!_.isNil(state.report)) {
                state.report.title = action.title
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_NEW_REPORT: (state, action) => {
            if (!_.isNil(state.report)) {
                state.report = emptyCohortReport(action.queryId)
            } else {
                console.warn('state.report is null')
            }

        },
        REPORT_ADD_INDEX_PERIOD: (state, action) => {
            if (!_.isNil(state.report)) {
                state.report.reportConfig.periods.push(action.period)
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_UPDATE_INDEX_PERIOD: (state, action) => {
            if (!_.isNil(state.report)) {
                const periods = state.report.reportConfig.periods
                const index = _.findIndex(periods, {
                    'id': action.period.id
                })
                periods[index] = action.period
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_DELETE_INDEX_PERIOD: (state, action) => {
            if (!_.isNil(state.report)) {
                const periods = state.report.reportConfig.periods
                _.remove(periods, {
                    'id': action.period.id
                })
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_CREATE_OR_UPDATE_GROUP: (state, action) => {
            if (!_.isNil(state.report)) {
                const reportConfig = state.report.reportConfig
                const group = action.group
                const groupArray = reportConfig[`group${group.level}` as keyof ReportConfig] as Group[]
                const index = _.findIndex(groupArray, { id: group.id })

                if (index > -1) {
                    groupArray[index] = group
                } else {
                    groupArray.push(group)
                }
                const newActiveGroup = emptyGroup(state.active.tab)
                state.active.group = newActiveGroup
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_DELETE_GROUP: (state, action) => {
            if (!_.isNil(state.report)) {
                const reportConfig = state.report.reportConfig
                const group = action.group
                const groupArray = reportConfig[`group${group.level}` as keyof ReportConfig] as Group[]
                _.remove(groupArray, { id: group.id })
                const newActiveGroup = emptyGroup(state.active.tab)
                state.active.group = newActiveGroup
            } else {
                console.warn('state.report is null')
            }
        },
        REPORT_CANCEL_GROUP: (state, action) => {
            const newActiveGroup = emptyGroup(state.active.tab)
            state.active.group = newActiveGroup
        },
        REPORT_SET_ACTIVE_TAB: (state, action) => {
            state.active.tab = action.tab
        },
        REPORT_SET_ACTIVE_GROUP: (state, action) => {
            state.active.group = action.group
            state.active.tab = action.group.level
        },
        REPORT_SET_ACTIVE_PERIOD: (state, action) => {
            state.active.period = action.period
        },
        REPORT_SET_SHOW_METRIC: (state, action) => {
            if (!_.isNil(state.report)) {
                state.report.reportConfig[action.metric as keyof ReportConfig] = action.checked
            } else {
                console.warn('state.report is null')
            }
        },
    }
)

export default hru